import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { SxProps } from '@mui/system';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import CheckboxInput from '~components/FormFields/v2/CheckboxInput';
import { useCompanyCurrency } from '~hooks/useCompanyCurrency/useCompanyCurrency';
import { SettlementLineItem } from '~hooks/useSettlementLineItems/models';

interface VendorDriverAdjustmentDTO {
  id: string;
  adjustPay: boolean;
  driver: VendorDriverAdjustmentDriver | null;
  vendor: VendorDriverAdjustmentVendor | null;
  adjustmentTotal: number;
  addOnDescription: string;
}

interface VendorDriverAdjustmentDriver {
  readonly id: string;
  readonly fullName: string;
}

interface VendorDriverAdjustmentVendor {
  readonly id: string;
  readonly name: string;
}

const generateDescription = (lineItem: SettlementLineItem) => {
  const jobIds = [
    ...new Set(lineItem.ids.map(({ friendlyJobId }) => friendlyJobId)),
  ].join(' | ');

  const loadOrdinalities = [
    ...new Set(
      lineItem.ids
        .filter(({ loadOrdinality }) => Boolean(loadOrdinality))
        .map(({ loadOrdinality }) => loadOrdinality),
    ),
  ].join(' | ');

  const ticketNumbers = lineItem.ticketNumbers?.join(' | ') ?? '-';

  // Only include load numbers if we have valid load ordinalities
  const loadSection = loadOrdinalities ? ` - Load ${loadOrdinalities}` : '';

  return `Adjustment for ${jobIds}${loadSection}, Ticket #${ticketNumbers}`;
};

export const convertSettlementLineItemToDTO = (
  lineItem: SettlementLineItem,
  index: number,
): VendorDriverAdjustmentDTO => {
  // Create a stable unique key based on line item data
  const uniqueKey = `settlement-line-item-${index}`;

  // Extract driver and vendor information from line item
  const driver = lineItem.driver ?? null;
  const vendor = lineItem.vendorJobAssignment?.vendorAccount ?? null;
  const delta =
    (lineItem.rebillTotalAmount ?? lineItem.totalAmount) - lineItem.totalAmount;
  const adjustmentTotal = delta;
  const addOnDescription = generateDescription(lineItem);

  return {
    id: uniqueKey,
    adjustPay: true,
    driver,
    vendor,
    adjustmentTotal,
    addOnDescription,
  };
};

interface VendorDriverAdjustmentRowProps {
  data: VendorDriverAdjustmentDTO;
  gridConfig: SxProps;
  disabled?: boolean;
  index: number;
}

const VendorDriverAdjustmentRow = ({
  data,
  gridConfig,
  disabled = false,
  index,
}: VendorDriverAdjustmentRowProps) => {
  const { control } = useFormContext();
  const { currencyFormatter } = useCompanyCurrency();

  return (
    <Box
      sx={{
        ...gridConfig,
        borderBottom: (theme) => `1px solid ${theme.semantic.border}`,
        alignItems: 'center',
      }}
    >
      <CheckboxInput
        name={`adjustments.${index}.adjustPay`}
        control={control}
        isDisabled={disabled}
        sx={{ p: 0 }}
      />
      <Typography variant="body2">{data.driver?.fullName ?? '-'}</Typography>
      <Typography variant="body2">{data.vendor?.name ?? '-'}</Typography>
      <Typography variant="body2">{currencyFormatter(data.adjustmentTotal)}</Typography>
      <Box sx={{ gridColumn: '5 / -1', display: 'flex', flexDirection: 'column' }}>
        <Controller
          name={`adjustments.${index}.addOnDescription`}
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              multiline
              minRows={1}
              maxRows={4}
              disabled={disabled}
              value={field.value}
              onChange={field.onChange}
              error={Boolean(fieldState.error)}
              sx={{
                fontSize: '11px',
                my: 0.5,
              }}
            />
          )}
        />
      </Box>
    </Box>
  );
};

export default VendorDriverAdjustmentRow;
